import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  createPushNotificationManagement,
  findPushNotificationManagement,
  patchPushNotificationManagement,
} from '../../store/pushNotificationManagement/api';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { omit } from 'lodash';
import NotificationFormModal from './NotificationFormModal';
import { IInitialValue } from '../../store/pushNotificationManagement/api/interface';
import notificationSchema from '../../validation/notificationSchema';
import {
  formatQatarDateTime,
  qatarDatetimeLocalToUtcIso,
} from '../../utils/qatarTime';
import { PushNotificationType } from '../../enum';
import { useEffect, useState } from 'react';
import Scrollbar from '../../components/Scrollbar';
import Loader from '../../components/Loader';
import {
  reset as resetPushNotificationManagement,
  setStatus as setPushNotificationManagementStatus,
} from '../../store/pushNotificationManagement/pushNotificationManagementSlice';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/pushNotificationManagement/pushNotificationManagementSlice';
import Searchbar from '../../components/Searchbar';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { toggle, onOpen, onClose } = useToggle();

  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.pushNotificationManagement);

  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPushNotificationManagement({
          limit,
          skip,
          ...(search && { search: search }),
          // type: type,
          // countOnly: false,
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetPushNotificationManagement());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IInitialValue>({
    initialValues: {
      id: '',
      title: '',
      message: '',
      type: '',
      zone: '',
      minOrderAmount: '',
      maxOrderAmount: '',
      categoryId: '',
      minOrderCount: '',
      maxOrderCount: '',
      language: '',
      notificationType: 'immediate',
      scheduledAt: '',
    },
    validationSchema: notificationSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchPushNotificationManagement(values)).unwrap();
        }
        if (!values.id) {
          const reqBody: any = omit(values, ['id']);
          if (reqBody.type === PushNotificationType.address) {
            delete reqBody.categoryId;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.category) {
            delete reqBody.zone;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.orderAmount) {
            delete reqBody.zone;
            delete reqBody.categoryId;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.orderCount) {
            delete reqBody.zone;
            delete reqBody.categoryId;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.language) {
            delete reqBody.zone;
            delete reqBody.categoryId;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.allUsers) {
            delete reqBody.categoryId;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            delete reqBody.zone;
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.allDownloadedUsers) {
            delete reqBody.categoryId;
            delete reqBody.minOrderAmount;
            delete reqBody.maxOrderAmount;
            delete reqBody.minOrderCount;
            delete reqBody.maxOrderCount;
            delete reqBody.zone;
            
            if (reqBody.notificationType === 'scheduled' && reqBody.scheduledAt) {
              reqBody.scheduledAt = qatarDatetimeLocalToUtcIso(reqBody.scheduledAt);
            } else {
              // Remove scheduledAt if not scheduled
              delete reqBody.scheduledAt;
            }
            
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
        }
        onClose();
        resetForm();
        // dispatch(resetPushNotificationManagement());
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setPushNotificationManagementStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Notification: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Notification'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpen}
              >
                {'New Notification'}
              </Button>
            }
          />

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Title'}</TableCell>
                      <TableCell align='center'>{'Message'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Details'}</TableCell>
                      <TableCell align='center'>{'Sender Name'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((pushNotificationManagement) => (
                        <TableRow key={pushNotificationManagement.id}>
                          <TableCell align='center'>
                            {pushNotificationManagement?.title}
                          </TableCell>
                          <TableCell align='center'>
                            {pushNotificationManagement?.message}
                          </TableCell>
                          <TableCell align='center'>
                            {pushNotificationManagement?.type}
                          </TableCell>

                          {pushNotificationManagement?.type ===
                            PushNotificationType.allUsers && (
                            <TableCell align='center'>-</TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.category && (
                            <TableCell align='center'>
                              {pushNotificationManagement?.category?.labelEn}
                            </TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.address && (
                            <TableCell align='center'>
                              {pushNotificationManagement?.zone?.map(
                                (zoneInfo) => (
                                  <Box>
                                    <Chip
                                      label={zoneInfo}
                                      sx={{ m: 0.2, fontWeight: 600 }}
                                    />
                                  </Box>
                                )
                              )}
                            </TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.language && (
                            <TableCell align='center'>
                              {pushNotificationManagement?.language}
                            </TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.allDownloadedUsers && (
                            <TableCell align='center'>
                              <Box>
                                <Typography variant='body2'>
                                  Language: {pushNotificationManagement?.language}
                                </Typography>
                                {pushNotificationManagement?.notificationType === 'scheduled' && pushNotificationManagement?.scheduledAt && (
                                  <>
                                    <Typography variant='caption' color='text.secondary' display='block'>
                                      Scheduled: {formatQatarDateTime(pushNotificationManagement.scheduledAt)}
                                    </Typography>
                                    {new Date(pushNotificationManagement.scheduledAt) <= new Date() && (
                                      <Typography variant='caption' color='success.main' display='block' sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Sent
                                      </Typography>
                                    )}
                                    {new Date(pushNotificationManagement.scheduledAt) > new Date() && (
                                      <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                                        Pending
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.orderAmount && (
                            <TableCell align='center'>
                              {pushNotificationManagement?.minOrderAmount}-
                              {pushNotificationManagement?.maxOrderAmount}
                            </TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.orderCount && (
                            <TableCell align='center'>
                              {pushNotificationManagement?.minOrderCount}-
                              {pushNotificationManagement?.maxOrderCount}
                            </TableCell>
                          )}

                          <TableCell align='center'>
                            {
                              pushNotificationManagement?.senderUserDetails
                                ?.firstName
                            }{' '}
                            {
                              pushNotificationManagement?.senderUserDetails
                                ?.lastName
                            }
                          </TableCell>
                          {/* <TableCell align='center'>
                            {country?.labelEn}
                          </TableCell>
                          <TableCell align='center'>
                            {country?.labelAr}
                          </TableCell>
                          <TableCell align='center'>
                            {' '}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: country.emoji,
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>{country?.code}</TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={country.activeState ? 'success' : 'error'}
                            >
                              {country.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(country)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell> */}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>

          <NotificationFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;